import React, { useState } from "react";
import { Star } from "lucide-react";
import cn from "./cn";

/**
 * StarRating — a five-star rating, interactive or read-only.
 *
 *   <StarRating value={r} onChange={setR} label="How clear was the trainer?" />
 *   <StarRating value={4.2} readOnly size="sm" />
 *
 * ── Semantics ────────────────────────────────────────────────────────────────
 * Interactive, this is a **radiogroup**: one choice out of five. Each star is a
 * `<button role="radio">` with roving tabindex, so Tab reaches the group once and
 * the arrow keys move within it — the behaviour a native radio group has and a
 * row of plain buttons does not.
 *
 * Read-only, it is a single `role="img"` with the value in its label. Five
 * decorative icons announced one at a time tell a screen-reader user nothing.
 *
 * ⚠️ `label` is the group's accessible name and matters more than it looks: on a
 * questionnaire every row is five identical stars, so without it the control
 * announces as "3 star" with no clue which question it belongs to.
 *
 * Props:
 *   value      — 0–5. Fractional values are allowed when `readOnly` (averages).
 *   onChange   — (next: number) => void. Omit for a display-only rating.
 *   label      — accessible name for the group. Required when interactive.
 *   readOnly   — render as a static display
 *   disabled   — interactive markup, but inert
 *   size       — "sm" | "md" | "lg" | "xl"  (default "md"); xl is a page's one question
 *   showValue  — append "3/5" (default true)
 *   max        — number of stars (default 5)
 */

const GLYPH_SIZE = { sm: 14, md: 18, lg: 22, xl: 32 };
const VALUE_TEXT_SIZE = { sm: "tw-text-[0.6875rem]", md: "tw-text-xs", lg: "tw-text-[0.8125rem]", xl: "tw-text-sm" };

const FILLED = "tw-text-warning";
const EMPTY = "tw-text-line";

const StarRating = ({
  value = 0,
  onChange,
  label,
  readOnly = false,
  disabled = false,
  size = "md",
  showValue = true,
  max = 5,
  className = "",
}) => {
  const [hover, setHover] = useState(0);
  const stars = Array.from({ length: max }, (_, i) => i + 1);
  const glyph = GLYPH_SIZE[size] || GLYPH_SIZE.md;
  const interactive = Boolean(onChange) && !readOnly;

  const valueText = showValue && value > 0 && (
    <span
      className={cn(
        "tw-ml-1.5 tw-min-w-7 tw-font-semibold tw-text-content-muted",
        VALUE_TEXT_SIZE[size] || VALUE_TEXT_SIZE.md
      )}
    >
      {Number.isInteger(value) ? value : value.toFixed(1)}/{max}
    </span>
  );

  if (!interactive) {
    return (
      <span
        className={cn("tw-inline-flex tw-items-center tw-gap-0.5", className)}
        role="img"
        aria-label={`${label ? `${label}: ` : ""}${value} out of ${max}`}
      >
        {stars.map((star) => (
          <Star
            key={star}
            size={glyph}
            className={star <= value ? FILLED : EMPTY}
            aria-hidden="true"
          />
        ))}
        {valueText}
      </span>
    );
  }

  // Roving tabindex: the checked star is the tab stop, or the first when unset.
  const tabStop = value > 0 ? value : 1;

  const move = (delta) => {
    const next = Math.min(max, Math.max(1, (value || 0) + delta));
    onChange(next);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault();
      move(1);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault();
      move(-1);
    }
  };

  return (
    <span
      role="radiogroup"
      aria-label={label}
      aria-disabled={disabled || undefined}
      className={cn("tw-inline-flex tw-items-center tw-gap-0.5", className)}
      onMouseLeave={() => setHover(0)}
    >
      {stars.map((star) => (
        <button
          key={star}
          type="button"
          role="radio"
          aria-checked={star === value}
          aria-label={`${star} of ${max}`}
          tabIndex={star === tabStop ? 0 : -1}
          disabled={disabled}
          onClick={() => onChange(star)}
          onKeyDown={handleKeyDown}
          onMouseEnter={() => !disabled && setHover(star)}
          className={cn(
            "tw-inline-flex tw-border-0 tw-bg-transparent tw-p-0.5 tw-transition-[color,transform]",
            "tw-focus-ring",
            "disabled:tw-cursor-default",
            "enabled:tw-cursor-pointer enabled:hover:tw-scale-110",
            star <= (hover || value) ? FILLED : EMPTY
          )}
        >
          <Star size={glyph} aria-hidden="true" />
        </button>
      ))}
      {valueText}
    </span>
  );
};

export default StarRating;
